import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card } from 'react-daisyui'
import { useNavigate, useParams } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import moment from 'moment'
import {
    AiOutlineArrowLeft,
    AiOutlineCalendar,
    AiOutlineCar,
    AiOutlineCheckCircle,
    AiOutlineClose,
    AiOutlineCreditCard,
    AiOutlineFileText,
    AiOutlineGift,
    AiOutlineHome,
    AiOutlineReload,
    AiOutlineShoppingCart,
    AiFillStar,
    AiOutlineStar,
} from 'react-icons/ai'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import DefaultProduct from '../../assets/default-product.jpg'

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const toNullableNumber = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string' && value.trim() === '') return null

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const formatCurrency = (value) => `Rp ${Intl.NumberFormat('en-ID').format(toNumber(value, 0))}`

const pickFirstValue = (values) => {
    for (const value of values) {
        if (value === null || value === undefined) continue
        if (typeof value === 'string' && value.trim() === '') continue
        return value
    }
    return null
}

const normalizeIdentifier = (value) => {
    if (value === null || value === undefined) return ''
    return `${value}`.trim()
}

const normalizeReviewStatus = (status) => `${status || ''}`
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')

const FINAL_REVIEW_APPROVAL_STATUSES = [
    'completed',
    'complete',
]

const SHIPPING_STATUS_NAME_BY_ID = {
    1: 'Dikemas',
    2: 'Mencari kurir',
    3: 'Diterima kurir',
    4: 'Menuju pickup',
    5: 'Barang diambil',
    6: 'Dikirim kurir',
    7: 'Diterima',
    8: 'Ditolak',
    9: 'Pickup gagal',
    10: 'On Hold',
    11: 'Cancelled',
    12: 'No Driver Found',
}

const PROVIDER_SHIPPING_STATUS_LABELS = {
    finding_driver: 'Mencari kurir',
    confirmed: 'Mencari kurir',
    driver_allocated: 'Diterima kurir',
    allocated: 'Diterima kurir',
    enroute_pickup: 'Menuju pickup',
    out_for_pickup: 'Menuju pickup',
    picked: 'Barang diambil',
    enroute_drop: 'Dikirim kurir',
    out_for_delivery: 'Dikirim kurir',
    completed: 'Diterima',
    delivered: 'Diterima',
    rejected: 'Ditolak',
    pickup_failed: 'Pickup gagal',
    on_hold: 'On Hold',
    cancelled: 'Cancelled',
    driver_not_found: 'No Driver Found',
    no_driver: 'No Driver Found',
    dnf: 'No Driver Found',
}

const HIDDEN_GOSEND_TIMELINE_PROCESSING_STATUSES = [
    'duplicate',
    'ignored',
    'failed',
    'unauthorized',
]

const SHIPPING_REASON_STATUS_IDS = [8, 11]
const SHIPPING_REASON_STATUS_KEYS = ['ditolak', 'rejected', 'cancelled', 'canceled']

const parsePayload = (payload) => {
    if (!payload) return {}
    if (typeof payload === 'object') return payload
    if (typeof payload !== 'string') return {}

    try {
        return JSON.parse(payload)
    } catch (error) {
        return {}
    }
}

const getTransactionPayload = (transaction) => (
    parsePayload(
        transaction?.transaction_payload ||
        transaction?.payload ||
        transaction?.request ||
        transaction?.transaction_request
    )
)

const getNestedMenu = (payload) => {
    if (!payload || typeof payload !== 'object') return null
    if (payload.menu && typeof payload.menu === 'object' && !Array.isArray(payload.menu)) return payload.menu
    if (Array.isArray(payload.menu)) return payload.menu[0] || null
    if (payload.item?.menu && typeof payload.item.menu === 'object' && !Array.isArray(payload.item.menu)) return payload.item.menu
    if (Array.isArray(payload.item?.menu)) return payload.item.menu[0] || null
    if (payload.product?.menu && typeof payload.product.menu === 'object' && !Array.isArray(payload.product.menu)) return payload.product.menu
    if (Array.isArray(payload.product?.menu)) return payload.product.menu[0] || null
    return null
}

const getDetailList = (transaction) => (
    Array.isArray(transaction?.transaction_detail) ? transaction.transaction_detail : []
)

const getItemName = (detail) => {
    const payload = parsePayload(detail?.transaction_detail_payload)
    return pickFirstValue([
        detail?.transaction_detail_item_name,
        detail?.item_name,
        detail?.item?.item_name,
        detail?.product?.item_name,
        payload?.item_name,
        payload?.item?.item_name,
        payload?.product?.item_name,
        payload?.menu?.item_name,
        payload?.menu?.item?.item_name,
    ]) || 'Produk tidak diketahui'
}

const getItemImage = (detail) => {
    const payload = parsePayload(detail?.transaction_detail_payload)
    const image = pickFirstValue([
        Array.isArray(payload?.item_image) ? payload.item_image[0] : payload?.item_image,
        Array.isArray(payload?.item?.item_image) ? payload.item.item_image[0] : payload?.item?.item_image,
        Array.isArray(payload?.product?.item_image) ? payload.product.item_image[0] : payload?.product?.item_image,
        Array.isArray(detail?.item_image) ? detail.item_image[0] : detail?.item_image,
    ])

    return image || DefaultProduct
}

const getItemId = (detail) => {
    const payload = parsePayload(detail?.transaction_detail_payload)
    return pickFirstValue([
        detail?.item_id,
        detail?.item?.item_id,
        detail?.product?.item_id,
        payload?.item_id,
        payload?.item?.item_id,
        payload?.product?.item_id,
        payload?.menu?.item_id,
        payload?.menu?.item?.item_id,
    ])
}

const getDetailReviewEligibility = (detail) => (
    detail?.review_eligibility ||
    detail?.reviewEligibility ||
    detail?.review ||
    null
)

const getReviewData = (detail) => {
    const eligibility = getDetailReviewEligibility(detail)
    const review = pickFirstValue([
        eligibility?.review,
        eligibility?.product_review,
        eligibility?.member_review,
        detail?.product_review,
        detail?.member_review,
        detail?.review_data,
    ])

    if (review && typeof review === 'object') return review
    return null
}

const getReviewRating = (detail) => {
    const review = getReviewData(detail)
    return toNullableNumber(pickFirstValue([
        review?.rating,
        review?.review_rating,
        review?.product_review_rating,
    ]))
}

const getReviewComment = (detail) => {
    const review = getReviewData(detail)
    return pickFirstValue([
        review?.comment,
        review?.review_comment,
        review?.product_review_comment,
    ])
}

const getReviewCreatedAt = (detail) => {
    const review = getReviewData(detail)
    return pickFirstValue([
        review?.created_at,
        review?.review_created_at,
        review?.updated_at,
    ])
}

const hasFinalReviewStatus = (candidates, finalStatuses) => (
    candidates.some((candidate) => {
        const normalized = normalizeReviewStatus(candidate)
        if (!normalized) return false
        if (finalStatuses.includes(normalized)) return true
        return finalStatuses.some((status) => normalized.includes(status))
    })
)

const isTransactionEligibleForReview = (transaction) => {
    const approvalCandidates = [
        transaction?.transaction_approve_status_name,
        transaction?.transaction_payment_status_name,
        transaction?.transaction_status_name,
        transaction?.payment_status,
        transaction?.approve_status,
        transaction?.status,
        transaction?.ms_transaction_status?.ms_transaction_status_name,
        transaction?.ms_payment_status?.ms_payment_status_name,
        transaction?.ms_approve_status?.ms_approve_status_name,
    ]
    return hasFinalReviewStatus(approvalCandidates, FINAL_REVIEW_APPROVAL_STATUSES)
}

const getDetailAlreadyReviewed = (detail) => {
    const eligibility = getDetailReviewEligibility(detail)
    if (eligibility && eligibility.already_reviewed !== undefined) {
        return Boolean(eligibility.already_reviewed)
    }
    if (eligibility && eligibility.alreadyReviewed !== undefined) {
        return Boolean(eligibility.alreadyReviewed)
    }
    return Boolean(detail?.already_reviewed || detail?.alreadyReviewed || detail?.reviewed || getReviewData(detail))
}

const getDetailCanReview = (transaction, detail) => {
    const eligibility = getDetailReviewEligibility(detail)
    if (eligibility && eligibility.can_review !== undefined) {
        return Boolean(eligibility.can_review)
    }
    if (eligibility && eligibility.canReview !== undefined) {
        return Boolean(eligibility.canReview)
    }

    return Boolean(
        getItemId(detail) &&
        isTransactionEligibleForReview(transaction) &&
        !getDetailAlreadyReviewed(detail)
    )
}

const getReviewReason = (transaction, detail) => {
    const eligibility = getDetailReviewEligibility(detail)
    if (eligibility?.reason) return eligibility.reason
    if (!getItemId(detail)) return 'item_id tidak tersedia'
    if (getDetailAlreadyReviewed(detail)) return 'Sudah direview'
    if (!isTransactionEligibleForReview(transaction)) return 'Transaksi belum selesai'
    return null
}

const getItemQuantity = (detail) => {
    const payload = parsePayload(detail?.transaction_detail_payload)
    return toNumber(pickFirstValue([
        detail?.transaction_detail_qty,
        detail?.transaction_detail_quantity,
        detail?.quantity,
        detail?.qty,
        detail?.item_quantity,
        detail?.menu_quantity,
        payload?.transaction_detail_qty,
        payload?.transaction_detail_quantity,
        payload?.quantity,
        payload?.qty,
        payload?.item_quantity,
        payload?.menu_qty,
        payload?.menu_quantity,
    ]), 1)
}

const getItemPrice = (detail) => {
    const payload = parsePayload(detail?.transaction_detail_payload)
    const menu = getNestedMenu(payload)
    const directPrice = pickFirstValue([
        detail?.transaction_detail_price,
        detail?.transaction_detail_item_price,
        detail?.transaction_detail_menu_price,
        detail?.price,
        detail?.current_price,
        detail?.item_price,
        detail?.menu_price,
        detail?.menu_platform_price,
        detail?.menu_platform_discount_price,
        payload?.transaction_detail_price,
        payload?.transaction_detail_item_price,
        payload?.transaction_detail_menu_price,
        payload?.price,
        payload?.current_price,
        payload?.item_price,
        payload?.menu_price,
        payload?.menu_platform_price,
        payload?.menu_platform_discount_price,
        payload?.menu?.menu_price,
        menu?.menu_price,
    ])
    const parsedDirectPrice = toNullableNumber(directPrice)
    if (parsedDirectPrice !== null && parsedDirectPrice > 0) return parsedDirectPrice

    const discountPrice = toNullableNumber(pickFirstValue([
        detail?.item_discount_price,
        detail?.menu_discount_price,
        detail?.transaction_detail_discount_price,
        payload?.item_discount_price,
        payload?.menu_discount_price,
        payload?.transaction_detail_discount_price,
        payload?.menu?.menu_discount_price,
        menu?.menu_discount_price,
    ]))
    const regularPrice = toNullableNumber(pickFirstValue([
        detail?.item_regular_price,
        detail?.menu_regular_price,
        detail?.transaction_detail_regular_price,
        payload?.item_regular_price,
        payload?.menu_regular_price,
        payload?.transaction_detail_regular_price,
        payload?.menu?.menu_regular_price,
        menu?.menu_regular_price,
    ]))

    if (discountPrice !== null && discountPrice > 0) return discountPrice
    if (regularPrice !== null) return regularPrice
    if (parsedDirectPrice !== null) return parsedDirectPrice

    return 0
}

const getItemSubtotal = (detail) => {
    const payload = parsePayload(detail?.transaction_detail_payload)
    const subtotal = pickFirstValue([
        detail?.transaction_detail_total,
        detail?.transaction_detail_subtotal,
        detail?.transaction_detail_amount,
        detail?.transaction_detail_total_amount,
        detail?.subtotal,
        detail?.sub_total,
        detail?.total,
        detail?.amount,
        payload?.transaction_detail_total,
        payload?.transaction_detail_subtotal,
        payload?.transaction_detail_amount,
        payload?.transaction_detail_total_amount,
        payload?.subtotal,
        payload?.sub_total,
        payload?.total,
        payload?.amount,
    ])

    const parsedSubtotal = toNullableNumber(subtotal)
    if (parsedSubtotal !== null && parsedSubtotal > 0) return parsedSubtotal

    return getItemQuantity(detail) * getItemPrice(detail)
}

const getStatusClass = (status) => {
    const normalized = `${status || ''}`.toLowerCase()
    if (['approved', 'success', 'paid', 'settlement', 'completed'].includes(normalized)) {
        return 'border-green-200 bg-green-50 text-green-700'
    }
    if (['pending', 'processing', 'diproses', 'progressing', 'verified'].includes(normalized)) {
        return 'border-amber-200 bg-amber-50 text-amber-700'
    }
    if (['failed', 'rejected', 'cancelled'].includes(normalized)) {
        return 'border-red-200 bg-red-50 text-red-700'
    }
    return 'border-slate-200 bg-slate-50 text-slate-700'
}

const normalizeShippingStatusKey = (value) => `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '_')

const getShippingStatusNameById = (statusId) => {
    const parsed = Number(statusId)
    if (!Number.isFinite(parsed)) return null
    return SHIPPING_STATUS_NAME_BY_ID[Math.floor(parsed)] || null
}

const formatShippingStatusName = (status) => {
    const value = `${status || ''}`.trim()
    if (!value) return null

    const mappedById = getShippingStatusNameById(value)
    if (mappedById) return mappedById

    return PROVIDER_SHIPPING_STATUS_LABELS[normalizeShippingStatusKey(value)] || value
}

const getShippingStatusName = (transaction) => {
    const transactionPayload = getTransactionPayload(transaction)
    const shippingDetail = parsePayload(
        transaction?.shipping_service_detail ||
        transactionPayload?.shipping_service_detail
    )
    const booking = shippingDetail?.booking || {}
    const bookingResponse = booking?.response || {}

    return formatShippingStatusName(pickFirstValue([
        transaction?.ms_shipping_status?.ms_shipping_status_name,
        transaction?.transaction_shipping_status_name,
        transaction?.shipping_status_name,
        transaction?.delivery_status_name,
        transaction?.transaction_delivery_status_name,
        transaction?.ms_delivery_status?.ms_delivery_status_name,
        transaction?.ms_delivery_status_name,
        transaction?.shipping_status_label,
        transaction?.shipping_status_text,
        transaction?.shipping_status,
        transaction?.delivery_status,
        shippingDetail?.shipping_status_name,
        shippingDetail?.delivery_status_name,
        shippingDetail?.status_name,
        shippingDetail?.status,
        shippingDetail?.booking_status,
        shippingDetail?.provider_status,
        bookingResponse?.status,
        bookingResponse?.booking_status,
        bookingResponse?.bookingStatus,
        getShippingStatusNameById(pickFirstValue([
            transaction?.ms_shipping_status_id,
            transaction?.shipping_status_id,
            transaction?.delivery_status_id,
            shippingDetail?.ms_shipping_status_id,
            shippingDetail?.shipping_status_id,
        ])),
    ])) || '-'
}

const getTransactionNumber = (transaction) => (
    pickFirstValue([
        transaction?.transaction_id,
        transaction?.transaction_number,
        transaction?.transaction_no,
        transaction?.number,
    ]) || '-'
)

const getTransactionReference = (transaction) => ({
    transaction_id: pickFirstValue([
        transaction?.transaction_id,
        transaction?.id,
    ]),
    transaction_number: pickFirstValue([
        transaction?.transaction_number,
        transaction?.transaction_no,
        transaction?.number,
    ]),
})

const getShippingAddress = (transaction) => (
    pickFirstValue([
        transaction?.shipping_destination_address,
        transaction?.transaction_shipping_address,
        transaction?.customer_address,
        transaction?.address,
        transaction?.shipping_address,
        transaction?.delivery_address,
        getTransactionPayload(transaction)?.shipping_destination_address,
    ]) || '-'
)

const getPaymentMethod = (transaction) => (
    pickFirstValue([
        transaction?.ms_payment?.ms_payment_name,
        transaction?.payment_method,
        transaction?.payment_name,
        transaction?.ms_payment_name,
        transaction?.transaction_payment_method_name,
    ]) || '-'
)

const getPaymentAllocation = (transaction) => {
    const transactionData = parsePayload(transaction?.transaction_data)
    const walletPayment = parsePayload(transactionData?.wallet_payment)
    const allocation = parsePayload(transaction?.payment_allocation || transactionData?.payment_allocation)
    const walletPaidAmount = toNullableNumber(pickFirstValue([
        transaction?.loyalty_balance_amount,
        transaction?.wallet_amount,
        allocation?.loyalty_balance_amount,
        allocation?.wallet_amount,
        walletPayment?.loyalty_balance_amount,
        walletPayment?.wallet_amount,
    ]))
    const qrisPaidAmount = toNullableNumber(pickFirstValue([
        transaction?.gateway_charge_amount,
        transaction?.qris_amount,
        allocation?.gateway_charge_amount,
        allocation?.qris_amount,
        walletPayment?.gateway_charge_amount,
        walletPayment?.qris_amount,
    ]))

    return {
        walletPaidAmount,
        qrisPaidAmount,
        hasAllocation: walletPaidAmount !== null || qrisPaidAmount !== null,
    }
}

const getTransactionVoucherInfo = (transaction) => {
    const transactionData = parsePayload(transaction?.transaction_data)
    const memberVoucherData = parsePayload(pickFirstValue([
        transaction?.member_voucher_data,
        transaction?.memberVoucherData,
        transactionData?.member_voucher_data,
        transactionData?.voucher_loyalty,
    ]))
    const transactionDataVoucher = parsePayload(transactionData?.voucher_loyalty)
    const voucher = (
        memberVoucherData?.voucher ||
        transactionDataVoucher?.voucher ||
        transaction?.member_voucher?.voucher ||
        transaction?.voucher ||
        {}
    )

    const memberVoucherId = pickFirstValue([
        transaction?.member_voucher_id,
        transaction?.memberVoucherId,
        memberVoucherData?.member_voucher_id,
        transactionDataVoucher?.member_voucher_id,
    ])
    const voucherId = pickFirstValue([
        transaction?.voucher_id,
        voucher?.voucher_id,
        memberVoucherData?.voucher_id,
        transactionDataVoucher?.voucher_id,
    ])
    const amount = toNullableNumber(pickFirstValue([
        memberVoucherData?.amount,
        transactionDataVoucher?.amount,
        transaction?.transaction_discount,
        transaction?.transaction_discount_amount,
        transaction?.discount,
        transaction?.voucher_discount,
    ]))
    const discountValue = toNullableNumber(pickFirstValue([
        memberVoucherData?.discount_value,
        transactionDataVoucher?.discount_value,
        voucher?.discount_value,
        voucher?.voucher_discount_fixed_amount,
        voucher?.voucher_discount_percentage,
    ]))
    const discountCalculationType = pickFirstValue([
        memberVoucherData?.discount_calculation_type,
        transactionDataVoucher?.discount_calculation_type,
        voucher?.discount_calculation_type,
    ])
    const voucherType = pickFirstValue([
        memberVoucherData?.voucher_type,
        transactionDataVoucher?.voucher_type,
        voucher?.type,
    ])
    const voucherName = pickFirstValue([
        voucher?.name,
        voucher?.voucher_name,
        memberVoucherData?.name,
        transactionDataVoucher?.name,
        memberVoucherId ? 'Voucher Loyalty' : null,
        voucherId ? 'Voucher' : null,
    ])
    const voucherSku = pickFirstValue([
        voucher?.sku,
        memberVoucherData?.sku,
        transactionDataVoucher?.sku,
    ])
    const partnerName = pickFirstValue([
        voucher?.partner?.name,
        voucher?.partner_name,
        memberVoucherData?.partner?.name,
        transactionDataVoucher?.partner?.name,
    ])
    const voucherCode = pickFirstValue([
        transaction?.voucher?.voucher_code,
        voucher?.voucher_code,
        memberVoucherData?.voucher_code,
        transactionDataVoucher?.voucher_code,
    ])

    const hasVoucher = Boolean(
        memberVoucherId ||
        voucherId ||
        voucherName ||
        voucherCode
    )
    if (!hasVoucher) return null

    const discountNumber = discountValue !== null && discountValue > 0 ? discountValue : amount
    let discountLabel = null
    if (voucherType === 'free_delivery') {
        discountLabel = 'Gratis ongkir'
    } else if (discountCalculationType === 'percentage' && discountNumber !== null) {
        discountLabel = `${Number(discountNumber.toFixed(2)).toLocaleString('id-ID')}%`
    } else if (discountNumber !== null && discountNumber > 0) {
        discountLabel = formatCurrency(discountNumber)
    } else if (voucherType === 'free') {
        discountLabel = 'Free item'
    }

    return {
        name: voucherName || 'Voucher',
        sku: voucherSku,
        partnerName,
        code: voucherCode,
        type: voucherType,
        memberVoucherId,
        voucherId,
        discountLabel,
    }
}

const getDeliveryMethod = (transaction) => (
    pickFirstValue([
        transaction?.ms_delivery?.ms_delivery_name,
        transaction?.delivery_name,
        transaction?.shipping_service,
        transaction?.shipping_method,
        transaction?.transaction_delivery_method_name,
    ]) || '-'
)

const getShippingTrackingNumber = (transaction) => (
    pickFirstValue([
        transaction?.shipping_resi,
        transaction?.tracking_number,
        transaction?.receipt_number,
        transaction?.resi,
        transaction?.awb,
    ])
)

const getCustomerReceiveConfirmationStatus = (transaction) => {
    const transactionData = parsePayload(transaction?.transaction_data)
    const confirmationData = parsePayload(transactionData?.retail_courier_confirmation)

    return pickFirstValue([
        transaction?.customer_receive_confirmation_status,
        transaction?.customerReceiveConfirmationStatus,
        confirmationData?.status,
    ])
}

const getCustomerReceiveConfirmedAt = (transaction) => {
    const transactionData = parsePayload(transaction?.transaction_data)
    const confirmationData = parsePayload(transactionData?.retail_courier_confirmation)

    return pickFirstValue([
        transaction?.customer_receive_confirmed_datetime,
        transaction?.customerReceiveConfirmedDatetime,
        confirmationData?.completed_at,
    ])
}

const getCustomerReceiveComplainedAt = (transaction) => {
    const transactionData = parsePayload(transaction?.transaction_data)
    const confirmationData = parsePayload(transactionData?.retail_courier_confirmation)

    return pickFirstValue([
        transaction?.customer_receive_complained_datetime,
        transaction?.customerReceiveComplainedDatetime,
        confirmationData?.complained_at,
    ])
}

const isReceiveConfirmationPending = (transaction) => {
    const confirmationStatus = normalizeReviewStatus(getCustomerReceiveConfirmationStatus(transaction))
    if (getCustomerReceiveConfirmedAt(transaction)) return false
    if (['completed', 'complete', 'auto completed'].includes(confirmationStatus)) return false
    if (confirmationStatus === 'complained' || getCustomerReceiveComplainedAt(transaction)) return true
    if (confirmationStatus === 'waiting') return true

    const approvalStatus = normalizeReviewStatus(transaction?.transaction_approve_status_name || transaction?.status)
    const shippingStatus = normalizeShippingStatusKey(getShippingStatusName(transaction))
    const shippingStatusId = Number(transaction?.ms_shipping_status_id || transaction?.shipping_status_id || transaction?.delivery_status_id)

    return (
        ['verified', 'progressing', 'courier process'].includes(approvalStatus) &&
        (shippingStatusId === 7 || ['diterima', 'received', 'delivered', 'completed', 'complete'].includes(shippingStatus))
    )
}

const normalizeWhatsappNumber = (value) => {
    let digits = `${value || ''}`.replace(/\D/g, '')
    if (!digits) return null
    if (digits.startsWith('0')) digits = `62${digits.slice(1)}`
    if (digits.startsWith('8')) digits = `62${digits}`
    return digits
}

const getStoreWhatsappNumber = (transaction) => normalizeWhatsappNumber(pickFirstValue([
    transaction?.store?.store_phone,
    transaction?.store?.store_whatsapp,
    transaction?.store_phone,
    transaction?.seller_store_phone,
]))

const buildComplainWhatsappUrl = (transaction, complainReason) => {
    const phone = getStoreWhatsappNumber(transaction)
    if (!phone) return null

    const transactionId = pickFirstValue([
        transaction?.transaction_id,
        transaction?.id,
    ]) || getTransactionNumber(transaction)
    const message = `Saya Belanja dgn ID Order ${transactionId} mengalami kendala ${complainReason}`
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
}

const maskPhoneLast4 = (value) => {
    const rawValue = `${value || ''}`.trim()
    if (!rawValue) return null

    const digits = rawValue.replace(/\D/g, '')
    const lastFourDigits = digits.slice(-4)
    if (!lastFourDigits) return null

    return `****${lastFourDigits}`
}

const resolveShippingResponseByMethod = (shippingDetail) => {
    const shippingResponse = shippingDetail?.response
    if (!shippingResponse || typeof shippingResponse !== 'object') return null

    const shipmentMethod = `${shippingDetail?.shipment_method || shippingDetail?.shiping_method || ''}`.trim()
    if (!shipmentMethod) return null

    if (shippingResponse[shipmentMethod]) return shippingResponse[shipmentMethod]

    const normalizedMethod = shipmentMethod.toLowerCase().replace(/\s+/g, '')
    const matchedEntry = Object.entries(shippingResponse).find(
        ([key]) => `${key}`.toLowerCase().replace(/\s+/g, '') === normalizedMethod
    )

    return matchedEntry ? matchedEntry[1] : null
}

const resolveShippingFee = (transaction) => {
    const transactionPayload = getTransactionPayload(transaction)
    const shippingDetail = transaction?.shipping_service_detail || transactionPayload?.shipping_service_detail

    const directFees = [
        transaction?.transaction_shipping_fee,
        transaction?.transaction_shipping_amount,
        transaction?.transaction_delivery_fee,
        transaction?.shipping_fee,
        transaction?.shipping_cost,
        transaction?.delivery_fee,
        transactionPayload?.transaction_shipping_fee,
        transactionPayload?.transaction_shipping_amount,
        transactionPayload?.shipping_fee,
        transactionPayload?.shipping_cost,
        transactionPayload?.delivery_fee,
    ]
        .map((value) => toNullableNumber(value))
        .filter((value) => value !== null)

    const positiveDirectFee = directFees.find((value) => value > 0)
    if (positiveDirectFee !== undefined) return positiveDirectFee

    const legacyShippingFee = toNullableNumber(shippingDetail?.expedition?.service?.cost?.value)
    if (legacyShippingFee !== null && legacyShippingFee > 0) return legacyShippingFee

    const selectedShippingResponse = resolveShippingResponseByMethod(shippingDetail)
    const selectedShippingFee = toNullableNumber(selectedShippingResponse?.price?.total_price)
    if (selectedShippingFee !== null && selectedShippingFee > 0) return selectedShippingFee

    const responseOptions = shippingDetail?.response
    if (responseOptions && typeof responseOptions === 'object') {
        const firstAvailableFee = Object.values(responseOptions)
            .map((option) => toNullableNumber(option?.price?.total_price))
            .find((value) => value !== null && value > 0)

        if (firstAvailableFee !== undefined) return firstAvailableFee
    }

    if (directFees.length > 0) return directFees[0]
    if (legacyShippingFee !== null) return legacyShippingFee
    if (selectedShippingFee !== null) return selectedShippingFee

    return 0
}

const resolveTransactionTotal = (transaction, fallbackTotal) => {
    const transactionPayload = getTransactionPayload(transaction)
    const totalValue = toNullableNumber(pickFirstValue([
        transaction?.transaction_total_amount,
        transaction?.total_amount,
        transaction?.grand_total,
        transaction?.total,
        transactionPayload?.transaction_total_amount,
        transactionPayload?.total_amount,
        transactionPayload?.grand_total,
        transactionPayload?.total,
    ]))

    if (totalValue !== null && totalValue > 0) return totalValue
    return fallbackTotal
}

const resolveTimelineMoment = (value) => {
    if (!value) return null

    const rawValue = `${value}`.trim()
    if (/^[0-9]+$/.test(rawValue)) {
        const numericValue = Number(rawValue)
        const parsedNumeric = moment(rawValue.length === 10 ? numericValue * 1000 : numericValue)
        return parsedNumeric.isValid() ? parsedNumeric : null
    }

    const parsed = moment(rawValue)
    return parsed.isValid() ? parsed : null
}

const formatTimelineDatetime = (value) => {
    if (!value) return '-'

    const parsed = resolveTimelineMoment(value)
    if (parsed) return parsed.format('DD MMM YYYY, HH:mm')
    return `${value}`
}

const formatProviderStatus = (value) => {
    const normalized = `${value || ''}`.trim()
    if (!normalized) return null
    return normalized.replace(/_/g, ' ')
}

const normalizeTimelineText = (value) => `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '_')

const shouldHideGosendTimelineLog = (log) => (
    HIDDEN_GOSEND_TIMELINE_PROCESSING_STATUSES.includes(
        normalizeTimelineText(log?.processing_status)
    )
)

const shouldShowShippingReason = (item) => {
    if (!item?.statusReason) return false
    if (SHIPPING_REASON_STATUS_IDS.includes(Number(item?.statusId))) return true

    return [
        item?.providerStatus,
        item?.statusName,
    ].some((value) => SHIPPING_REASON_STATUS_KEYS.includes(normalizeTimelineText(value)))
}

const getGosendWebhookLog = (transaction) => {
    if (Array.isArray(transaction?.gosend_webhook_log)) return transaction.gosend_webhook_log
    if (Array.isArray(transaction?.gosend_webhook_logs)) return transaction.gosend_webhook_logs
    return []
}

const getTransactionShippingHistory = (transaction) => (
    Array.isArray(transaction?.transaction_shipping_history)
        ? transaction.transaction_shipping_history
        : []
)

const resolveTimelineStatusId = (item) => {
    const parsed = Number(pickFirstValue([
        item?.mapped_shipping_status_id,
        item?.ms_shipping_status_id,
        item?.shipping_status_id,
        item?.delivery_status_id,
        item?.ms_shipping_status?.ms_shipping_status_id,
    ]))

    return Number.isFinite(parsed) ? Math.floor(parsed) : null
}

const getTimelineDriver = (item) => {
    const driver = item?.driver || {}
    const name = pickFirstValue([
        item?.driver_name,
        item?.driverName,
        driver?.name,
        driver?.driver_name,
        driver?.driverName,
    ])
    const phone = pickFirstValue([
        item?.driver_phone,
        item?.driverPhone,
        driver?.phone,
        driver?.driver_phone,
        driver?.driverPhone,
    ])

    if (!name && !phone) return null
    return { name, phone }
}

const buildGosendTimelineItems = (transaction) => (
    getGosendWebhookLog(transaction)
        .map((log, index) => {
            if (shouldHideGosendTimelineLog(log)) return null

            const statusId = resolveTimelineStatusId(log)
            const providerStatus = log?.provider_status || log?.providerStatus
            const statusName = formatShippingStatusName(pickFirstValue([
                log?.mapped_shipping_status_name,
                log?.shipping_status_name,
                log?.delivery_status_name,
                getShippingStatusNameById(statusId),
                providerStatus,
            ]))
            const driver = getTimelineDriver(log)
            const statusReason = pickFirstValue([
                log?.status_reason,
                log?.statusReason,
                log?.cancellation_reason,
                log?.cancellationReason,
                log?.cancel_reason,
                log?.cancelReason,
                log?.cancelled_reason,
                log?.cancelledReason,
                log?.canceled_reason,
                log?.canceledReason,
                log?.rejection_reason,
                log?.rejectionReason,
                log?.reject_reason,
                log?.rejectReason,
                log?.rejected_reason,
                log?.rejectedReason,
                log?.reason,
                log?.message,
            ])

            if (!statusName && !providerStatus) return null

            return {
                key: `gosend-${log?.gosend_webhook_log_id || index}`,
                source: 'gosend',
                statusId,
                statusName: statusName || '-',
                providerStatus,
                processingStatus: log?.processing_status || null,
                datetime: pickFirstValue([
                    log?.status_changed_datetime,
                    log?.processed_datetime,
                    log?.received_datetime,
                    log?.event_timestamp,
                ]),
                orderNo: pickFirstValue([
                    log?.provider_booking_id,
                    log?.shipping_resi,
                    log?.order_no,
                    log?.orderNo,
                ]),
                driverName: driver?.name || null,
                driverPhone: driver?.phone || null,
                statusReason: statusReason ? `${statusReason}` : null,
            }
        })
        .filter(Boolean)
)

const buildShippingHistoryTimelineItems = (transaction) => (
    getTransactionShippingHistory(transaction)
        .map((history, index) => {
            const statusId = resolveTimelineStatusId(history)
            const statusName = formatShippingStatusName(pickFirstValue([
                history?.ms_shipping_status?.ms_shipping_status_name,
                history?.shipping_status_name,
                history?.delivery_status_name,
                getShippingStatusNameById(statusId),
            ]))

            if (!statusName) return null

            return {
                key: `history-${history?.transaction_shipping_history_id || index}`,
                source: 'history',
                statusId,
                statusName,
                providerStatus: null,
                processingStatus: null,
                datetime: pickFirstValue([
                    history?.created_at,
                    history?.created_datetime,
                    history?.updated_at,
                    history?.updated_datetime,
                ]),
                orderNo: null,
                driverName: null,
                driverPhone: null,
                statusReason: null,
            }
        })
        .filter(Boolean)
)

const getTimelineDuplicateKey = (item) => ([
    item?.statusId || normalizeTimelineText(item?.statusName),
    normalizeTimelineText(item?.providerStatus),
    normalizeTimelineText(item?.orderNo),
].join('|'))

const dedupeConsecutiveTimelineItems = (items = []) => (
    items.filter((item, index) => (
        index === 0 ||
        getTimelineDuplicateKey(item) !== getTimelineDuplicateKey(items[index - 1])
    ))
)

const buildShippingTimeline = (transaction) => {
    if (!transaction) return []

    const gosendItems = buildGosendTimelineItems(transaction)
    const historyItems = buildShippingHistoryTimelineItems(transaction)
    const items = gosendItems.length > 0 ? gosendItems : historyItems

    if (items.length === 0) {
        const currentStatus = getShippingStatusName(transaction)
        if (!currentStatus || currentStatus === '-') return []

        return [{
            key: 'current-shipping-status',
            source: 'current',
            statusId: toNullableNumber(transaction?.ms_shipping_status_id),
            statusName: currentStatus,
            providerStatus: null,
            processingStatus: null,
            datetime: transaction?.transaction_updated_datetime || transaction?.updated_at || transaction?.transaction_created_datetime,
            orderNo: getShippingTrackingNumber(transaction),
            driverName: transaction?.gosend_driver?.name || transaction?.gosend_pickup_driver?.name || null,
            driverPhone: transaction?.gosend_driver?.phone || transaction?.gosend_pickup_driver?.phone || null,
            statusReason: null,
        }]
    }

    const sortedItems = [...items].sort((left, right) => {
        const leftTime = resolveTimelineMoment(left.datetime)
        const rightTime = resolveTimelineMoment(right.datetime)

        if (leftTime && rightTime) return leftTime.valueOf() - rightTime.valueOf()
        if (leftTime) return -1
        if (rightTime) return 1
        return 0
    })

    return dedupeConsecutiveTimelineItems(sortedItems)
}

const getReceivedAt = (shippingTimeline) => {
    const receivedItem = [...shippingTimeline]
        .reverse()
        .find((item) => (
            item?.statusId === 7 ||
            ['diterima', 'received', 'delivered', 'completed', 'complete'].includes(
                normalizeShippingStatusKey(item?.statusName || item?.providerStatus)
            )
        ))

    return receivedItem?.datetime || null
}

const getPaymentStatus = (transaction) => {
    const paymentStatus = pickFirstValue([
        transaction?.transaction_payment_status_name,
        transaction?.payment_status,
        transaction?.ms_payment_status?.ms_payment_status_name,
    ])
    const normalizedPaymentStatus = normalizeReviewStatus(paymentStatus)
    const normalizedApprovalStatus = normalizeReviewStatus(
        transaction?.transaction_approve_status_name || transaction?.status
    )
    const shippingStatusId = Number(
        transaction?.ms_shipping_status_id || transaction?.shipping_status_id || transaction?.delivery_status_id
    )
    const normalizedShippingStatus = normalizeShippingStatusKey(getShippingStatusName(transaction))
    const hasReceivedShipping = (
        shippingStatusId === 7 ||
        ['diterima', 'received', 'delivered', 'completed', 'complete'].includes(normalizedShippingStatus)
    )

    // Pesanan yang sudah diproses atau diterima kurir tidak lagi menunggu pembayaran,
    // walaupun label status pembayaran dari API masih mengembalikan "processing".
    if (
        ['processing', 'process', 'pending'].includes(normalizedPaymentStatus) &&
        (
            ['verified', 'progressing', 'completed', 'complete'].includes(normalizedApprovalStatus) ||
            hasReceivedShipping
        )
    ) {
        return 'Lunas'
    }

    return paymentStatus || transaction?.transaction_approve_status_name || transaction?.status || '-'
}

const getTimelineMarkerClass = (item, isLatest) => {
    if ([7].includes(item?.statusId) || normalizeShippingStatusKey(item?.statusName) === 'diterima') {
        return 'border-green-200 bg-green-600'
    }
    if ([8, 9, 11].includes(item?.statusId)) {
        return 'border-red-200 bg-red-600'
    }
    if ([12].includes(item?.statusId)) {
        return 'border-amber-200 bg-amber-500'
    }
    if (isLatest) return 'border-blue-200 bg-blue-600'
    return 'border-slate-200 bg-slate-400'
}

function TransactionDetail() {
    const [transaction, setTransaction] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [reviewModal, setReviewModal] = useState({
        open: false,
        detail: null,
    })
    const [reviewRating, setReviewRating] = useState(0)
    const [reviewComment, setReviewComment] = useState('')
    const [reviewSubmitting, setReviewSubmitting] = useState(false)
    const [receiveActionSubmitting, setReceiveActionSubmitting] = useState(null)
    const [receiveActionResolved, setReceiveActionResolved] = useState(false)
    const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false)
    const [complainDialogOpen, setComplainDialogOpen] = useState(false)
    const [complainReason, setComplainReason] = useState('')

    const { transactionIdentifier } = useParams()
    const navigate = useNavigate()

    const decodedIdentifier = useMemo(() => {
        try {
            return decodeURIComponent(transactionIdentifier || '')
        } catch (error) {
            return transactionIdentifier || ''
        }
    }, [transactionIdentifier])

    const loadTransactionDetail = async () => {
        if (!decodedIdentifier) {
            setError('ID transaksi tidak tersedia')
            setLoading(false)
            return
        }

        setLoading(true)
        setError('')
        setReceiveActionResolved(false)
        setCompleteConfirmOpen(false)

        const params = {
            access: 'auth',
            method: API.TRANSACTION_GET.type,
            url: API.TRANSACTION_GET.url,
            reqBody: {
                transaction_number: decodedIdentifier,
                transaction_id: decodedIdentifier,
            },
        }

        try {
            const response = await Api(params)
            if (response?.status) {
                setTransaction(response.data)
            } else {
                setTransaction(null)
                setError(response?.message || 'Transaksi tidak ditemukan')
            }
        } catch (requestError) {
            setTransaction(null)
            setError(requestError?.message || 'Gagal memuat detail transaksi')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadTransactionDetail()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [decodedIdentifier])

    const closeReviewModal = () => {
        if (reviewSubmitting) return
        setReviewModal({
            open: false,
            detail: null,
        })
        setReviewRating(0)
        setReviewComment('')
    }

    const handleOpenReviewModal = (detail) => {
        if (!getDetailCanReview(transaction, detail)) {
            const reason = getReviewReason(transaction, detail)
            if (reason) toast.error(reason)
            return
        }

        setReviewModal({
            open: true,
            detail,
        })
        setReviewRating(0)
        setReviewComment('')
    }

    const markTransactionItemReviewed = (itemId, review) => {
        const targetItemId = normalizeIdentifier(itemId)
        setTransaction((currentTransaction) => {
            if (!currentTransaction) return currentTransaction

            return {
                ...currentTransaction,
                transaction_detail: getDetailList(currentTransaction).map((detail) => {
                    if (normalizeIdentifier(getItemId(detail)) !== targetItemId) return detail

                    const currentEligibility = getDetailReviewEligibility(detail) || {}
                    return {
                        ...detail,
                        review_eligibility: {
                            ...currentEligibility,
                            item_id: itemId,
                            can_review: false,
                            already_reviewed: true,
                            review,
                            reason: 'Sudah direview',
                        },
                    }
                }),
            }
        })
    }

    const handleSubmitReview = async (event) => {
        event.preventDefault()
        if (!transaction || !reviewModal.detail) return

        if (reviewRating < 1 || reviewRating > 5) {
            toast.error('Pilih rating 1 sampai 5')
            return
        }

        const itemId = getItemId(reviewModal.detail)
        const transactionReference = getTransactionReference(transaction)

        if (!itemId) {
            toast.error('item_id tidak tersedia')
            return
        }

        setReviewSubmitting(true)
        const params = {
            access: 'auth',
            url: API.MEMBER_PRODUCT_REVIEW.url,
            method: API.MEMBER_PRODUCT_REVIEW.type,
            reqBody: {
                transaction_id: transactionReference.transaction_id,
                transaction_number: transactionReference.transaction_number,
                item_id: itemId,
                rating: reviewRating,
                comment: reviewComment.trim(),
            },
        }

        try {
            const response = await Api(params)
            if (response?.status) {
                const submittedReview = response?.data?.review || {
                    item_id: itemId,
                    transaction_id: transactionReference.transaction_id,
                    transaction_number: transactionReference.transaction_number,
                    rating: reviewRating,
                    comment: reviewComment.trim(),
                    created_at: new Date().toISOString(),
                }

                toast.success(response.message || 'Review berhasil dikirim')
                markTransactionItemReviewed(itemId, submittedReview)
                setReviewModal({
                    open: false,
                    detail: null,
                })
                setReviewRating(0)
                setReviewComment('')
            } else {
                toast.error(response?.message || 'Review gagal dikirim')
            }
        } catch (requestError) {
            toast.error('Terjadi kesalahan saat mengirim review')
        } finally {
            setReviewSubmitting(false)
        }
    }

    const closeCompleteConfirmDialog = () => {
        if (receiveActionSubmitting) return
        setCompleteConfirmOpen(false)
    }

    const handleCompleteTransaction = () => {
        if (!transaction || receiveActionSubmitting) return

        const transactionReference = getTransactionReference(transaction)
        if (!transactionReference.transaction_number) {
            toast.error('Nomor transaksi tidak tersedia')
            return
        }

        setCompleteConfirmOpen(true)
    }

    const submitCompleteTransaction = async () => {
        if (!transaction || receiveActionSubmitting) return

        const transactionReference = getTransactionReference(transaction)
        if (!transactionReference.transaction_number) {
            toast.error('Nomor transaksi tidak tersedia')
            return
        }

        setReceiveActionSubmitting('complete')
        const params = {
            access: 'auth',
            url: API.TRANSACTION_COMPLETE.url,
            method: API.TRANSACTION_COMPLETE.type,
            reqBody: {
                transaction_number: transactionReference.transaction_number,
            },
        }

        try {
            const response = await Api(params)
            if (response?.status) {
                toast.success(response.message || 'Transaksi berhasil diselesaikan')
                setCompleteConfirmOpen(false)
                setReceiveActionResolved(true)
                if (response.data) {
                    setTransaction({
                        ...transaction,
                        ...response.data,
                        transaction_approve_status_name: response.data.transaction_approve_status_name || 'completed',
                        customer_receive_confirmation_status: response.data.customer_receive_confirmation_status || 'completed',
                        customer_receive_confirmed_datetime: response.data.customer_receive_confirmed_datetime || new Date().toISOString(),
                    })
                }
                else await loadTransactionDetail()
            } else {
                toast.error(response?.message || 'Gagal menyelesaikan transaksi')
            }
        } catch (requestError) {
            toast.error('Terjadi kesalahan saat menyelesaikan transaksi')
        } finally {
            setReceiveActionSubmitting(null)
        }
    }

    const closeComplainDialog = () => {
        if (receiveActionSubmitting) return
        setComplainDialogOpen(false)
        setComplainReason('')
    }

    const handleComplainTransaction = () => {
        if (!transaction || receiveActionSubmitting) return

        const transactionReference = getTransactionReference(transaction)
        if (!transactionReference.transaction_number) {
            toast.error('Nomor transaksi tidak tersedia')
            return
        }

        setComplainReason('')
        setComplainDialogOpen(true)
    }

    const submitComplainTransaction = async (event) => {
        event.preventDefault()
        if (!transaction || receiveActionSubmitting) return

        const reason = complainReason.trim()
        if (!reason) {
            toast.error('Alasan complain wajib diisi')
            return
        }

        const transactionReference = getTransactionReference(transaction)
        if (!transactionReference.transaction_number) {
            toast.error('Nomor transaksi tidak tersedia')
            return
        }

        setReceiveActionSubmitting('complain')
        const params = {
            access: 'auth',
            url: API.TRANSACTION_COMPLAIN.url,
            method: API.TRANSACTION_COMPLAIN.type,
            reqBody: {
                transaction_number: transactionReference.transaction_number,
                complain_note: reason,
            },
        }

        try {
            const response = await Api(params)
            if (response?.status) {
                const nextTransaction = {
                    ...transaction,
                    ...(response.data || {}),
                    customer_receive_confirmation_status: 'complained',
                    customer_receive_complained_datetime: response?.data?.customer_receive_complained_datetime || new Date().toISOString(),
                }
                setTransaction(nextTransaction)
                setComplainDialogOpen(false)
                setComplainReason('')
                toast.success(response.message || 'Complain berhasil dicatat')

                const whatsappUrl = buildComplainWhatsappUrl(nextTransaction, reason)
                if (whatsappUrl) {
                    window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
                } else {
                    toast.error('Nomor WhatsApp toko tidak tersedia')
                }
            } else {
                toast.error(response?.message || 'Gagal mengirim complain')
            }
        } catch (requestError) {
            toast.error('Terjadi kesalahan saat mengirim complain')
        } finally {
            setReceiveActionSubmitting(null)
        }
    }

    const renderStars = (rating, interactive = false) => (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((value) => {
                const active = value <= toNumber(rating, 0)
                if (interactive) {
                    return (
                        <button
                            key={value}
                            type="button"
                            className="flex h-10 w-10 items-center justify-center rounded-md text-2xl text-yellow-500 hover:bg-yellow-50"
                            onClick={() => setReviewRating(value)}
                            aria-label={`Rating ${value}`}
                        >
                            {active ? <AiFillStar /> : <AiOutlineStar />}
                        </button>
                    )
                }

                return active
                    ? <AiFillStar key={value} className="text-sm text-yellow-500" />
                    : <AiOutlineStar key={value} className="text-sm text-yellow-500" />
            })}
        </div>
    )

    const renderReviewSection = (detail) => {
        const alreadyReviewed = getDetailAlreadyReviewed(detail)
        const canReview = getDetailCanReview(transaction, detail)
        const reason = getReviewReason(transaction, detail)
        const rating = getReviewRating(detail)
        const comment = getReviewComment(detail)
        const createdAt = getReviewCreatedAt(detail)

        if (alreadyReviewed) {
            return (
                <div className="mt-3 rounded-md border border-green-100 bg-green-50 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-green-700">Sudah direview</span>
                        {createdAt ? (
                            <span className="text-[10px] text-green-700">
                                {moment(createdAt).format('DD MMM YYYY')}
                            </span>
                        ) : null}
                    </div>
                    {rating ? (
                        <div className="mt-2">{renderStars(rating)}</div>
                    ) : null}
                    {comment ? (
                        <p className="mt-2 text-xs leading-relaxed text-slate-700">{comment}</p>
                    ) : (
                        <p className="mt-2 text-xs text-slate-500">Review sudah diberikan.</p>
                    )}
                </div>
            )
        }

        if (canReview) {
            return (
                <div className="mt-3 flex justify-end">
                    <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-md border border-yellow-200 bg-yellow-50 px-3 py-1.5 text-xs font-semibold text-yellow-700 hover:bg-yellow-100"
                        onClick={() => handleOpenReviewModal(detail)}
                    >
                        Beri Review
                    </button>
                </div>
            )
        }

        if (!reason) return null

        return (
            <p className="mt-2 text-[11px] text-slate-500">{reason}</p>
        )
    }

    const detailList = getDetailList(transaction)
    const transactionNumber = getTransactionNumber(transaction)
    const createdAt = transaction?.transaction_created_datetime || transaction?.created_at
    const approveStatus = transaction?.transaction_approve_status_name || transaction?.status || '-'
    const paymentStatus = getPaymentStatus(transaction)
    const shippingStatus = getShippingStatusName(transaction)
    const subtotal = detailList.reduce((total, detail) => total + getItemSubtotal(detail), 0)
    const shippingFee = resolveShippingFee(transaction)
    const discount = toNumber(pickFirstValue([
        transaction?.transaction_discount_amount,
        transaction?.transaction_discount,
        transaction?.discount,
        transaction?.voucher_discount,
    ]), 0)
    const totalAmount = resolveTransactionTotal(transaction, subtotal + shippingFee - discount)
    const voucherInfo = getTransactionVoucherInfo(transaction)
    const paymentAllocation = getPaymentAllocation(transaction)
    const trackingNumber = getShippingTrackingNumber(transaction)
    const shippingTimeline = buildShippingTimeline(transaction)
    const canConfirmReceived = !receiveActionResolved && isReceiveConfirmationPending(transaction)
    const receivedAt = getReceivedAt(shippingTimeline)
    const receiveConfirmationDue = receivedAt
        ? moment(receivedAt).add(1, 'hour').toISOString()
        : null

    const renderShippingTimeline = () => {
        if (shippingTimeline.length === 0) {
            return (
                <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 py-4 text-center text-xs text-slate-500">
                    Riwayat pengiriman belum tersedia
                </p>
            )
        }

        return (
            <div className="space-y-0">
                {shippingTimeline.map((item, index) => {
                    const isLatest = index === shippingTimeline.length - 1
                    const maskedDriverPhone = maskPhoneLast4(item.driverPhone)
                    const driverText = item.driverName || item.driverPhone
                        ? `${item.driverName || 'Driver'}${maskedDriverPhone ? ` (${maskedDriverPhone})` : ''}`
                        : null
                    const providerStatus = formatProviderStatus(item.providerStatus)
                    const statusReason = shouldShowShippingReason(item) ? `${item.statusReason}`.trim() : null

                    return (
                        <div key={item.key} className="relative flex gap-3 pb-4 last:pb-0">
                            {index < shippingTimeline.length - 1 && (
                                <span className="absolute left-[7px] top-5 h-full w-px bg-slate-200" />
                            )}
                            <span className={`relative z-10 mt-1 h-4 w-4 shrink-0 rounded-full border-4 ${getTimelineMarkerClass(item, isLatest)}`} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-slate-900">{item.statusName}</p>
                                        {providerStatus ? (
                                            <p className="mt-0.5 capitalize text-[11px] text-slate-500">
                                                Provider: {providerStatus}
                                            </p>
                                        ) : null}
                                    </div>
                                    <p className="shrink-0 text-right text-[11px] leading-5 text-slate-500">
                                        {formatTimelineDatetime(item.datetime)}
                                    </p>
                                </div>

                                {(driverText || item.orderNo || item.processingStatus || statusReason) ? (
                                    <div className="mt-2 space-y-1 text-[11px] leading-relaxed text-slate-600">
                                        {statusReason ? (
                                            <p>
                                                Alasan: <span className="font-semibold text-slate-800">{statusReason}</span>
                                            </p>
                                        ) : null}
                                        {driverText ? (
                                            <p>
                                                Driver: <span className="font-semibold text-slate-800">{driverText}</span>
                                            </p>
                                        ) : null}
                                        {item.orderNo ? (
                                            <p>
                                                Order GoSend: <span className="font-mono font-semibold text-slate-800">{item.orderNo}</span>
                                            </p>
                                        ) : null}
                                        {item.processingStatus ? (
                                            <p className="capitalize">
                                                Log: <span className="font-semibold text-slate-800">{item.processingStatus}</span>
                                            </p>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <ToastContainer autoClose={1000} />
            <div className="mx-auto max-w-sm px-4 py-5 pb-24">
                <div className="mb-4 flex items-center gap-3">
                    <Button
                        size="sm"
                        className="h-10 w-10 min-h-0 rounded-full border border-slate-200 bg-white p-0 text-slate-700 hover:bg-slate-50"
                        onClick={() => navigate('/transaction/list')}
                    >
                        <AiOutlineArrowLeft className="text-lg text-white" />
                    </Button>
                    <div className="min-w-0">
                        <h1 className="text-base font-semibold text-slate-900">Detail Transaksi</h1>
                        <p className="truncate text-xs text-slate-500">{transactionNumber}</p>
                    </div>
                </div>

                {loading ? (
                    <div className="animate-pulse space-y-4">
                        <div className="h-28 rounded-lg bg-slate-200" />
                        <div className="h-44 rounded-lg bg-slate-200" />
                        <div className="h-36 rounded-lg bg-slate-200" />
                    </div>
                ) : error ? (
                    <Card className="rounded-lg border border-red-100 bg-white shadow-sm">
                        <Card.Body className="items-center py-10 text-center">
                            <AiOutlineFileText className="mb-2 text-5xl text-red-300" />
                            <p className="text-sm font-semibold text-slate-800">{error}</p>
                            <div className="mt-4 flex gap-2">
                                <Button
                                    size="sm"
                                    className="rounded-md border border-slate-200 bg-white text-xs text-slate-700"
                                    onClick={() => navigate('/transaction/list')}
                                >
                                    Kembali
                                </Button>
                                <Button
                                    size="sm"
                                    className="rounded-md border-none bg-blue-600 text-xs text-white"
                                    onClick={loadTransactionDetail}
                                >
                                    <AiOutlineReload className="mr-1" />
                                    Coba lagi
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        <Card className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <Card.Body className="space-y-3 p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-xs text-slate-500">Nomor Transaksi</p>
                                        <p className="break-all font-mono text-sm font-semibold text-slate-900">
                                            {transactionNumber}
                                        </p>
                                    </div>
                                    <span className={`shrink-0 rounded-full border px-2 py-1 text-[11px] font-semibold ${getStatusClass(approveStatus)}`}>
                                        {approveStatus}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 gap-2 text-xs text-slate-600">
                                    <div className="flex items-center gap-2">
                                        <AiOutlineCalendar />
                                        {createdAt ? moment(createdAt).format('DD MMM YYYY, HH:mm') : '-'}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <AiOutlineCreditCard />
                                        Status: {paymentStatus}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <AiOutlineCar />
                                        Pengiriman: {shippingStatus}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <Card.Body className="space-y-3 p-4">
                                <div className="flex items-center gap-2">
                                    <AiOutlineShoppingCart className="text-slate-600" />
                                    <h2 className="text-sm font-semibold text-slate-900">Produk</h2>
                                </div>

                                {detailList.length === 0 ? (
                                    <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 py-4 text-center text-xs text-slate-500">
                                        Detail produk tidak tersedia
                                    </p>
                                ) : (
                                    <div className="divide-y divide-slate-100">
                                        {detailList.map((detail, index) => {
                                            const qty = getItemQuantity(detail)
                                            const price = getItemPrice(detail)
                                            const itemSubtotal = getItemSubtotal(detail)
                                            const displayPrice = price > 0
                                                ? price
                                                : (qty > 0 ? itemSubtotal / qty : itemSubtotal)

                                            return (
                                                <div key={index} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                                                    <img
                                                        src={getItemImage(detail)}
                                                        alt={getItemName(detail)}
                                                        className="h-14 w-14 shrink-0 rounded-lg object-cover"
                                                        onError={(event) => {
                                                            event.target.src = DefaultProduct
                                                        }}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="line-clamp-2 text-sm font-semibold text-slate-900">
                                                            {getItemName(detail)}
                                                        </p>
                                                        <p className="mt-1 text-xs text-slate-500">
                                                            {qty} x {formatCurrency(displayPrice)}
                                                        </p>
                                                        <p className="mt-1 text-right text-sm font-semibold text-slate-900">
                                                            {formatCurrency(itemSubtotal)}
                                                        </p>
                                                        {renderReviewSection(detail)}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        <Card className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <Card.Body className="space-y-3 p-4">
                                <div className="flex items-center gap-2">
                                    <AiOutlineCreditCard className="text-slate-600" />
                                    <h2 className="text-sm font-semibold text-slate-900">Pembayaran</h2>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between gap-3 text-slate-600">
                                        <span>Subtotal</span>
                                        <span>{formatCurrency(subtotal)}</span>
                                    </div>
                                    <div className="flex justify-between gap-3 text-slate-600">
                                        <span>Ongkos kirim</span>
                                        <span>{formatCurrency(shippingFee)}</span>
                                    </div>
                                    {discount > 0 && (
                                        <div className="flex justify-between gap-3 text-green-600">
                                            <span>Diskon</span>
                                            <span>-{formatCurrency(discount)}</span>
                                        </div>
                                    )}
                                    {voucherInfo && (
                                        <div className="rounded-md border border-green-100 bg-green-50 px-3 py-2 text-xs text-green-700">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex min-w-0 items-center gap-2">
                                                    <AiOutlineGift className="shrink-0 text-base" />
                                                    <div className="min-w-0">
                                                        <p className="font-semibold">Voucher digunakan</p>
                                                        <p className="mt-0.5 line-clamp-2 font-medium text-green-900">
                                                            {voucherInfo.name}
                                                        </p>
                                                    </div>
                                                </div>
                                                {voucherInfo.discountLabel ? (
                                                    <span className="shrink-0 rounded-full bg-white px-2 py-1 font-semibold text-green-700">
                                                        {voucherInfo.discountLabel}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {(voucherInfo.partnerName || voucherInfo.sku || voucherInfo.code || voucherInfo.memberVoucherId) ? (
                                                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-green-700">
                                                    {voucherInfo.partnerName ? (
                                                        <span>{voucherInfo.partnerName}</span>
                                                    ) : null}
                                                    {voucherInfo.sku ? (
                                                        <span>SKU: <span className="font-mono font-semibold">{voucherInfo.sku}</span></span>
                                                    ) : null}
                                                    {voucherInfo.code ? (
                                                        <span>Kode: <span className="font-mono font-semibold">{voucherInfo.code}</span></span>
                                                    ) : null}
                                                    {voucherInfo.memberVoucherId ? (
                                                        <span>Member voucher: <span className="font-mono font-semibold">#{voucherInfo.memberVoucherId}</span></span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                    <div className="border-t border-slate-100 pt-2">
                                        <div className="flex justify-between gap-3 text-base font-bold text-slate-900">
                                            <span>Total</span>
                                            <span>{formatCurrency(totalAmount)}</span>
                                        </div>
                                    </div>
                                    {(paymentAllocation.walletPaidAmount || 0) > 0 || (paymentAllocation.qrisPaidAmount || 0) > 0 ? (
                                        <div className="space-y-1 border-t border-dashed border-slate-100 pt-2 text-xs text-slate-600">
                                            {(paymentAllocation.walletPaidAmount || 0) > 0 && (
                                                <div className="flex justify-between gap-3">
                                                    <span>Dibayar melalui Saldo</span>
                                                    <span className="font-semibold">{formatCurrency(paymentAllocation.walletPaidAmount)}</span>
                                                </div>
                                            )}
                                            {(paymentAllocation.qrisPaidAmount || 0) > 0 && (
                                                <div className="flex justify-between gap-3">
                                                    <span>Dibayar melalui QRIS</span>
                                                    <span className="font-semibold">{formatCurrency(paymentAllocation.qrisPaidAmount)}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                    <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                        Metode pembayaran: <span className="font-semibold">{getPaymentMethod(transaction)}</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        <Card className="rounded-lg border border-slate-200 bg-white shadow-sm">
                            <Card.Body className="space-y-3 p-4">
                                <div className="flex items-center gap-2">
                                    <AiOutlineHome className="text-slate-600" />
                                    <h2 className="text-sm font-semibold text-slate-900">Pengiriman</h2>
                                </div>
                                <div className="space-y-2 text-xs text-slate-600">
                                    <div>
                                        <p className="font-semibold text-slate-800">Alamat tujuan</p>
                                        <p className="mt-1 leading-relaxed">{getShippingAddress(transaction)}</p>
                                    </div>
                                    <div className="rounded-md bg-slate-50 px-3 py-2">
                                        Metode pengiriman: <span className="font-semibold">{getDeliveryMethod(transaction)}</span>
                                    </div>
                                    <div className="rounded-md bg-slate-50 px-3 py-2">
                                        Status pengiriman: <span className="font-semibold">{shippingStatus}</span>
                                    </div>
                                    {trackingNumber && (
                                        <div className="rounded-md bg-slate-50 px-3 py-2">
                                            Resi: <span className="font-mono font-semibold">
                                                {trackingNumber}
                                            </span>
                                        </div>
                                    )}
                                    <div className="border-t border-slate-100 pt-3">
                                        <div className="mb-3 flex items-center justify-between gap-3">
                                            <p className="font-semibold text-slate-800">Riwayat status pengiriman</p>
                                            {shippingTimeline.length > 0 ? (
                                                <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                                                    {shippingTimeline.length} update
                                                </span>
                                            ) : null}
                                        </div>
                                        {renderShippingTimeline()}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        {canConfirmReceived && (
                            <Card className="rounded-lg border border-green-100 bg-white shadow-sm">
                                <Card.Body className="space-y-3 p-4">
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">Konfirmasi Pesanan</p>
                                        {receiveConfirmationDue ? (
                                            <p className="mt-1 text-xs text-slate-500">
                                                Batas konfirmasi: {moment(receiveConfirmationDue).format('DD MMM YYYY, HH:mm')}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Button
                                            className="rounded-md border-none bg-green-600 text-white hover:bg-green-700 disabled:bg-green-300"
                                            disabled={Boolean(receiveActionSubmitting)}
                                            onClick={handleCompleteTransaction}
                                        >
                                            <AiOutlineCheckCircle className="mr-2" />
                                            {receiveActionSubmitting === 'complete' ? 'Memproses...' : 'Selesai'}
                                        </Button>
                                        <Button
                                            className="rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:text-red-300"
                                            disabled={Boolean(receiveActionSubmitting)}
                                            onClick={handleComplainTransaction}
                                        >
                                            <AiOutlineClose className="mr-2" />
                                            {receiveActionSubmitting === 'complain' ? 'Memproses...' : 'Complain'}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        <Button
                            className="w-full rounded-md border-none bg-blue-600 text-white hover:bg-blue-700"
                            onClick={() => navigate('/transaction/list')}
                        >
                            <AiOutlineCheckCircle className="mr-2" />
                            Kembali ke Daftar Transaksi
                        </Button>
                    </div>
                )}
            </div>

            {completeConfirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-4">
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="complete-transaction-title"
                        className="w-full max-w-sm rounded-2xl bg-white shadow-xl"
                    >
                        <div className="space-y-4 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p id="complete-transaction-title" className="text-base font-semibold text-slate-900">
                                        Selesaikan pesanan?
                                    </p>
                                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                                        Pastikan barang sudah diterima sebelum transaksi diselesaikan.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-lg text-slate-600 hover:bg-slate-200"
                                    onClick={closeCompleteConfirmDialog}
                                    disabled={Boolean(receiveActionSubmitting)}
                                    aria-label="Tutup dialog"
                                >
                                    <AiOutlineClose />
                                </button>
                            </div>

                            <div className="rounded-lg bg-slate-50 px-3 py-2">
                                <p className="text-[11px] text-slate-500">Nomor Transaksi</p>
                                <p className="mt-0.5 break-all font-mono text-xs font-semibold text-slate-800">
                                    {transactionNumber}
                                </p>
                            </div>

                            <div className="flex gap-2 pt-1">
                                <button
                                    type="button"
                                    className="h-10 flex-1 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:text-slate-300"
                                    onClick={closeCompleteConfirmDialog}
                                    disabled={Boolean(receiveActionSubmitting)}
                                >
                                    Batal
                                </button>
                                <button
                                    type="button"
                                    className="h-10 flex-1 rounded-lg bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:bg-green-300"
                                    onClick={submitCompleteTransaction}
                                    disabled={Boolean(receiveActionSubmitting)}
                                >
                                    {receiveActionSubmitting === 'complete' ? 'Memproses...' : 'Selesaikan'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {complainDialogOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-4">
                    <form
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="complain-transaction-title"
                        className="w-full max-w-sm rounded-2xl bg-white shadow-xl"
                        onSubmit={submitComplainTransaction}
                    >
                        <div className="space-y-4 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p id="complain-transaction-title" className="text-base font-semibold text-slate-900">Ajukan complain</p>
                                    <p className="mt-1 text-xs leading-relaxed text-slate-500">Jelaskan kendala Anda. Pesan ini akan dikirim ke WhatsApp toko.</p>
                                </div>
                                <button
                                    type="button"
                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-lg text-slate-600 hover:bg-slate-200"
                                    onClick={closeComplainDialog}
                                    disabled={Boolean(receiveActionSubmitting)}
                                    aria-label="Tutup dialog"
                                >
                                    <AiOutlineClose />
                                </button>
                            </div>
                            <textarea
                                autoFocus
                                required
                                maxLength={1000}
                                value={complainReason}
                                onChange={(event) => setComplainReason(event.target.value)}
                                disabled={Boolean(receiveActionSubmitting)}
                                placeholder="Contoh: Pesanan yang diterima tidak sesuai"
                                className="min-h-[112px] w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="h-10 flex-1 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:text-slate-300"
                                    onClick={closeComplainDialog}
                                    disabled={Boolean(receiveActionSubmitting)}
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="h-10 flex-1 rounded-lg bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-red-300"
                                    disabled={Boolean(receiveActionSubmitting) || !complainReason.trim()}
                                >
                                    {receiveActionSubmitting === 'complain' ? 'Mengirim...' : 'Oke'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            {reviewModal.open && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 py-4">
                    <div className="w-full max-w-sm rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
                        <form onSubmit={handleSubmitReview} className="space-y-4 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <img
                                        src={reviewModal.detail ? getItemImage(reviewModal.detail) : DefaultProduct}
                                        alt={reviewModal.detail ? getItemName(reviewModal.detail) : 'Produk'}
                                        className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
                                        onError={(event) => {
                                            event.target.src = DefaultProduct
                                        }}
                                    />
                                    <div className="min-w-0">
                                        <p className="line-clamp-2 text-sm font-semibold text-gray-900">
                                            {reviewModal.detail ? getItemName(reviewModal.detail) : 'Produk'}
                                        </p>
                                        <p className="mt-1 font-mono text-[11px] text-gray-500">
                                            {transactionNumber}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-lg leading-none text-gray-600 hover:bg-gray-200"
                                    onClick={closeReviewModal}
                                    disabled={reviewSubmitting}
                                    aria-label="Tutup modal review"
                                >
                                    <AiOutlineClose />
                                </button>
                            </div>

                            <div>
                                <label className="mb-2 block text-xs font-semibold text-gray-700">
                                    Rating
                                </label>
                                {renderStars(reviewRating, true)}
                            </div>

                            <div>
                                <label className="mb-2 block text-xs font-semibold text-gray-700">
                                    Komentar
                                </label>
                                <textarea
                                    className="min-h-[96px] w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                                    value={reviewComment}
                                    onChange={(event) => setReviewComment(event.target.value)}
                                    placeholder="Bagikan pengalaman Anda"
                                    maxLength={1000}
                                    disabled={reviewSubmitting}
                                />
                            </div>

                            <div className="flex gap-2 pt-1">
                                <button
                                    type="button"
                                    className="h-10 flex-1 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                    onClick={closeReviewModal}
                                    disabled={reviewSubmitting}
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="h-10 flex-1 rounded-lg bg-yellow-500 text-sm font-semibold text-white hover:bg-yellow-600 disabled:bg-gray-300 disabled:text-gray-500"
                                    disabled={reviewSubmitting || reviewRating < 1}
                                >
                                    {reviewSubmitting ? 'Mengirim...' : 'Kirim Review'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default TransactionDetail
